import { useState } from 'react'
import { RotateCcw, Save, ChevronDown, ChevronUp } from 'lucide-react'
import { clsx } from 'clsx'
import type { Formula } from '../formulaTypes'

interface FormulaCardProps {
  formulaKey: string
  formula: Formula
  currentValue: string
  isCustom: boolean
  onSave: (key: string, value: string) => void
  onReset: (key: string) => void
}

export function FormulaCard({
  formulaKey,
  formula,
  currentValue,
  isCustom,
  onSave,
  onReset,
}: FormulaCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [editValue, setEditValue] = useState(currentValue)
  const [hasChanges, setHasChanges] = useState(false)

  const handleInputChange = (value: string) => {
    setEditValue(value)
    setHasChanges(value !== currentValue)
  }

  const handleSave = () => {
    if (editValue.trim() && hasChanges) {
      onSave(formulaKey, editValue.trim())
      setHasChanges(false)
    }
  }

  const handleReset = () => {
    onReset(formulaKey)
    setEditValue(currentValue)
    setHasChanges(false)
  }

  const categoryLabel =
    formula.category === 'key-metric'
      ? 'Key Metric'
      : formula.category === 'profitability'
        ? 'Profitability'
        : 'Assessment'

  return (
    <div
      className={clsx(
        'bg-[var(--bg-tertiary)] border rounded-lg transition-all',
        isCustom ? 'border-warning/50' : 'border-[var(--border-color)]',
        hasChanges && 'border-primary/50'
      )}
    >
      {/* Header */}
      <div
        className="p-3 cursor-pointer flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[var(--text-primary)]">{formula.name}</span>
              {isCustom && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-warning/20 text-warning">
                  Modified
                </span>
              )}
            </div>
            <span
              className={clsx(
                'text-[10px] px-1.5 py-0.5 rounded mt-1 inline-block',
                formula.isHighlight ? 'bg-primary/20 text-primary' : 'bg-[var(--bg-secondary)] text-[var(--text-muted)]'
              )}
            >
              {categoryLabel}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp size={16} className="text-[var(--text-muted)]" />
          ) : (
            <ChevronDown size={16} className="text-[var(--text-muted)]" />
          )}
        </div>
      </div>

      {/* Current Formula Display (always visible) */}
      <div className="px-3 pb-3">
        <div className="text-[10px] text-[var(--text-muted)] mb-1">Current Formula:</div>
        <code className="block text-[11px] text-primary bg-[var(--bg-secondary)] px-2 py-1.5 rounded font-mono break-all">
          {currentValue}
        </code>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-[var(--border-color)] pt-3">
          {/* Description */}
          <p className="text-[11px] text-[var(--text-secondary)]">{formula.description}</p>

          {/* Edit Field */}
          <div>
            <label className="text-[10px] text-[var(--text-muted)] mb-1 block">Edit Formula:</label>
            <textarea
              value={editValue}
              onChange={(e) => handleInputChange(e.target.value)}
              className="w-full px-2 py-1.5 text-[11px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] font-mono focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!hasChanges || !editValue.trim()}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded transition-colors',
                hasChanges && editValue.trim()
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] cursor-not-allowed'
              )}
            >
              <Save size={12} />
              Apply
            </button>
            <button
              onClick={handleReset}
              disabled={!isCustom}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded transition-colors',
                isCustom
                  ? 'bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--border-color)]'
                  : 'bg-[var(--bg-secondary)] text-gray-600 cursor-not-allowed'
              )}
            >
              <RotateCcw size={12} />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
